import { useState, useRef, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import Popover from '@mui/material/Popover';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemButton from '@mui/material/ListItemButton';
import Avatar from '@mui/material/Avatar';
import Fuse from 'fuse.js';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';

import { RootState } from '../../store/store';
import { useAppSelector, useAppDispatch } from '../../store/hooks/useTypedSelector';
import { fetchProduct } from '../../store/slices/productSlice';
import { product } from '../../types';

const SearchContainer = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const dispatch = useAppDispatch();
  const products = useAppSelector((state: RootState) => state.product.products);
  const [searchResults, setSearchResults] = useState<Array<product>>([]);
  const [query, setQuery] = useState('');
  const fuse = new Fuse(products, {
    keys: ['name', 'description'],
    includeScore: true,
  });
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const id = open ? 'simple-popover' : undefined;
  const paperRef = useRef<HTMLDivElement>(null);
  const handleFormClick = (e: React.FormEvent) => {
    e.preventDefault();
    setAnchorEl(paperRef.current);
    const results = fuse.search(query);
    const searchResults = results.map((character) => character.item);
    setSearchResults(searchResults);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleOnSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };
  const productClickHandler = (product: product) => {
    navigate(`/product-details/${product.id}`, { state: product });
    setQuery('');
    handleClose();
  };
  useEffect(() => {
    if (!products.length) {
      dispatch(fetchProduct());
    }
  }, []);
  let searchContainerWidth = window.innerWidth - 40;
  if (searchContainerWidth >= 900) {
    searchContainerWidth = 900;
  }

  return (
    <div className='col-md-7'>
      <div className='d-flex'>
        <form onSubmit={handleFormClick}>
          <Paper
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: searchContainerWidth,
            }}
            ref={paperRef}
          >
            <InputBase
              sx={{ flex: 1, pl: 2, height: '40px' }}
              placeholder='Search any product...'
              inputProps={{ 'aria-label': 'Search any product...' }}
              className={isDarkMode ? 'bg-dark' : 'bg-white'}
              value={query}
              onChange={handleOnSearch}
              data-testid='search-product'
            />
            <IconButton
              type='submit'
              aria-label='search'
              className={isDarkMode ? 'bg-dark' : 'bg-white'}
              sx={{
                flex: 0.05,
                borderRadius: 'unset',
              }}
              data-testid='search-button'
            >
              <SearchIcon />
            </IconButton>
          </Paper>
        </form>
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <List
            sx={{
              width: searchContainerWidth,
              overflow: 'auto',
              maxHeight: 500,
              bgcolor: 'background.paper',
            }}
          >
            {searchResults.map((product, index) => {
              return (
                <div key={index}>
                  <ListItem alignItems='flex-start'>
                    <ListItemButton
                      onClick={() => productClickHandler(product)}
                      data-testid={`product-${product.id}`}
                    >
                      <ListItemAvatar>
                        <Avatar alt={product.name} src={product.imageSrc} />
                      </ListItemAvatar>
                      <ListItemText primary={product.name} secondary={product.description} />
                    </ListItemButton>
                  </ListItem>
                  {index !== searchResults.length - 1 && <Divider variant='inset' component='li' />}
                </div>
              );
            })}
            {searchResults.length === 0 && (
              <Typography
                gutterBottom
                variant='h6'
                component='div'
                className='d-flex justify-content-center'
              >
                Product Not Found!
              </Typography>
            )}
          </List>
        </Popover>
      </div>
    </div>
  );
};

export default SearchContainer;
